import {Route, Routes, useNavigate, useLocation} from "react-router-dom";
import {useEffect, useState, useContext} from 'react';
import Layout from './components/common/Layout/Layout';
import NotificationsContainer from './components/NotificationDashboard/NotificationsContainer';
import Dashboard from './components/MyAccount/Dashboard';
import Settings from './components/Settings/OrgSetting';
import Users from './components/Settings/users';
import Plans from './components/Settings/plans';
import OrganisationDetail from './components/Settings/OrganisationDetail';
import Login from './components/Login/Login';
import PrivateRoute from './components/route/PrivateRoute';
import PublicRoute from './components/route/PublicRoute';
import ModifyDetails from './components/ModifySchoolDetails/ModifyDetails';
import AppUsersPage from './components/AppUsers/AppUsersPage';
import AuthContext from './store/auth-context';
import { checkOrganisationReadiness } from './services/organization.service';
import { initializeVersion } from './services/version.service';
import VersionChecker from './components/common/VersionChecker';
import { USERS_API_BASE_URL } from './constants';
import axios from 'axios';


function App() {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [isOrgReady, setIsOrgReady] = useState(true);
    const [missingCriticalItems, setMissingCriticalItems] = useState<string[]>([]);
    const [isCheckingReadiness, setIsCheckingReadiness] = useState(true);
    const [canAccessNotifications, setCanAccessNotifications] = useState(true);
    const [isServerDown, setIsServerDown] = useState(false);

    // Initialize version checking on app startup
    useEffect(() => {
        initializeVersion().catch(console.error);
    }, []);

    // Check if API server is reachable
    useEffect(() => {
        const checkServerStatus = async () => {
            try {
                await axios.get(`${USERS_API_BASE_URL}/status`, { timeout: 5000 });
                setIsServerDown(false);
            } catch {
                setIsServerDown(true);
            }
        };
        checkServerStatus();
    }, []);

    // Redirect root path based on user type
    useEffect(() => {
        if (window.location.pathname === '/') {
            // INTERNALADMIN and OWNER should go to Organisation page by default
            if (authCtx.userData?.usertype === 'INTERNALADMIN' || authCtx.userData?.usertype === 'OWNER') {
                navigate('/Organisation#organisations');
            } else {
                navigate('/notifications');
            }
        }
    }, [authCtx.userData?.usertype, navigate]);

    // Check organisation readiness on mount and when token changes
    useEffect(() => {
        const checkReadiness = async () => {
            // Skip check if on login page or not logged in
            if (location.pathname === '/login' || !authCtx.isLoggedIn || !authCtx.token) {
                setIsCheckingReadiness(false);
                return;
            }

          
            // INTERNALADMIN and OWNER users need an orgId (from impersonation) to access notifications
            if (authCtx.userData?.usertype === 'INTERNALADMIN' || authCtx.userData?.usertype === 'OWNER') {
                setIsCheckingReadiness(false);
                setIsOrgReady(true);
                // Only allow notifications access if they have an orgId (impersonated)
                const hasOrgId = authCtx.userData?.orgId && authCtx.userData?.orgId !== '';
                setCanAccessNotifications(hasOrgId);
                return;
            }

            // Skip check for super admin/internal users without org
            if (!authCtx.userData?.orgId) {
                setIsCheckingReadiness(false);
                setIsOrgReady(true);
                return;
            }

            try {
                const data = await checkOrganisationReadiness(authCtx.token);
                
                // Non-critical items that only block /notifications but don't show popup
                const nonCriticalItems = [
                    'Categories (at least one required)',
                    'Tags (at least one tag required in categories)',
                    'Active Verification Codes'
                ];
                
                // If ready is false, determine if it's due to critical or non-critical items
                if (!data.ready) {
                    const missingItems = data.missingItems || [];
                    
                    // Check if all missing items are non-critical
                    const onlyNonCritical = missingItems.length > 0 && 
                        missingItems.every((item: string) => nonCriticalItems.includes(item));
                    
                    if (onlyNonCritical) {
                        // Only non-critical items missing - allow access to other areas but block /notifications
                        setIsOrgReady(true);
                        setCanAccessNotifications(false);
                        setMissingCriticalItems(missingItems);
                    } else {
                        // Critical items missing - show full blocker
                        setIsOrgReady(false);
                        setCanAccessNotifications(false);
                        setMissingCriticalItems(missingItems);
                    }
                } else {
                    setIsOrgReady(true);
                    setCanAccessNotifications(true);
                    setMissingCriticalItems([]);
                }
            } catch (error) {
                console.error('Error checking organisation readiness:', error);
                // On error, allow access but log the issue
                setIsOrgReady(true);
                setCanAccessNotifications(true);
            } finally {
                setIsCheckingReadiness(false);
            }
        };

        checkReadiness();
    }, [authCtx.isLoggedIn, authCtx.token, location.pathname, authCtx.userData?.orgId]);

    // Navigate to settings if org is not ready and user tries to access other pages
    useEffect(() => {
        if (!isCheckingReadiness && !isOrgReady && location.pathname !== '/Organisation' && location.pathname !== '/login') {
            navigate('/Organisation#users');
        }
    }, [isOrgReady, isCheckingReadiness, location.pathname, navigate]);

    // Block access to /notifications if setup is incomplete (even for non-critical items)
    useEffect(() => {
        if (!isCheckingReadiness && !canAccessNotifications && location.pathname === '/notifications') {
            navigate('/Organisation#users');
        }
    }, [canAccessNotifications, isCheckingReadiness, location.pathname, navigate]);

    return (
        <>
            {/* Version checker - shows banner when update available */}
            <VersionChecker />

            {/* Server down overlay */}
            {isServerDown && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgb(153, 164, 177)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '40px',
                        maxWidth: '500px',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            color: '#333'
                        }}>Server is not responding</h2>
                        <p style={{
                            fontSize: '16px',
                            color: '#666',
                            lineHeight: '1.6'
                        }}>Please try after sometime</p>
                    </div>
                </div>
            )}
            
            {/* Blocking overlay for incomplete setup */}
            {!isCheckingReadiness && !isOrgReady && location.pathname !== '/login' && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgb(153, 164, 177)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '40px',
                        maxWidth: '500px',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '20px'
                        }}><i>⚠️</i></div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            color: '#333'
                        }}>Organisation Setup In Progress</h2>
                        <p style={{
                            fontSize: '16px',
                            color: '#666',
                            marginBottom: '20px',
                            lineHeight: '1.6'
                        }}>
                            The organisation setup is still in progress. Please wait until our team completes the necessary configurations. 
                            <i style={{
                                fontSize: '16px', display: 'block', marginTop: '10px'
                            }}>Thank you for your patience!</i>
                        </p>
                        {/* <div style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            padding: '15px',
                            marginBottom: '25px'
                        }}>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {missingCriticalItems.map((item, index) => (
                                    <li key={index} style={{
                                        fontSize: '14px',
                                        color: '#dc3545',
                                        fontWeight: '500',
                                        marginBottom: index < missingCriticalItems.length - 1 ? '8px' : '0'
                                    }}>
                                        ❌ {item}
                                    </li>
                                ))}
                            </ul>
                        </div> */}
                        {/* <button
                            onClick={() => navigate('/Organisation#users')}
                            style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '12px 30px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                        >
                            Go to Settings
                        </button> */}
                    </div>
                </div>
            )}
            
            <Layout>
            <Routes>
                <Route element={<NotificationsContainer/>} path="/notifications" />
                <Route element={<ModifyDetails/>} path="/ModifyDetails" />
                <Route element={<Users/>} path="/users" />
                {/* <PrivateRoute component={Plans} path="/plans" exact /> */}
                <Route element={<Dashboard/>} path="/dashboard" />
                <Route element={<OrganisationDetail/>} path="/organisation/detail/:orgUuid" />
                <Route element={<Settings/>} path="/Organisation" />
                <Route element={<AppUsersPage/>} path="/app-users/all" />
                <Route element={<Login/>} path="/login" />
            </Routes>
        </Layout>
        </>
    );
}
export default App;
